import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Calendar, Plus, Image as ImageIcon, Edit2, List, CalendarDays } from 'lucide-react';
import { useAuth } from '@/contexts/auth-context';
import { useToast } from '@/hooks/use-toast';
import { ToastAction } from '@/components/ui/toast';
import { formatApiError } from '@/services/api';
import { useLocation } from 'wouter';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { getCommunityEvents, addCommunityEvent, uploadEventBackground, Timestamp } from '@/lib/firebase';
import { logger } from '@/lib/logger';

interface CommunityEvent {
  id: string;
  title: string;
  description?: string;
  type: string;
  startDate?: any;
  endDate?: any;
  month?: string;
  url?: string;
  enableMessages?: boolean;
  enableImages?: boolean;
  imageUrl?: string;
  createdAt?: any;
  status?: 'Scheduled' | 'Current' | 'Completed';
}

const eventSchema = z.object({
  title: z.string().min(1, 'Title is required'),
  description: z.string().optional(),
  type: z.enum(['liveQA', 'stepsChallenge', 'yoga', 'content']),
  tag: z.string().optional(),
  dateMode: z.enum(['month', 'dateRange']).optional(),
  selectedMonth: z.string().optional(),
  startDate: z.string().optional(),
  endDate: z.string().optional(),
  startTime: z.string().optional(),
  endTime: z.string().optional(),
  url: z.string().optional(),
  enableMessages: z.boolean(),
  enableImages: z.boolean(),
  imageUrl: z.string().optional(),
});

type EventFormData = z.infer<typeof eventSchema>;

interface CalendarViewProps {
  events: CommunityEvent[];
  onEventClick: (eventId: string) => void;
  onStateChange?: (date: Date, viewMode: CalendarViewMode) => void;
}

type CalendarViewMode = 'month' | '3-month' | 'year';

function CalendarView({ events, onEventClick, onStateChange }: CalendarViewProps) {
  // Get calendar state from URL params or use defaults
  const urlParams = new URLSearchParams(window.location.search);
  const savedDate = urlParams.get('calendarDate');
  const savedCalendarView = urlParams.get('calendarView') as CalendarViewMode;
  
  const [currentDate, setCurrentDate] = useState(savedDate ? new Date(savedDate) : new Date());
  const [viewMode, setViewMode] = useState<CalendarViewMode>(savedCalendarView || 'month');
  
  const currentYear = currentDate.getFullYear();
  const currentMonth = currentDate.getMonth();
  
  // Get first day of the month and number of days
  const firstDayOfMonth = new Date(currentYear, currentMonth, 1);
  const lastDayOfMonth = new Date(currentYear, currentMonth + 1, 0);
  const daysInMonth = lastDayOfMonth.getDate();
  const startingDayOfWeek = firstDayOfMonth.getDay();
  
  // Generate calendar days
  const calendarDays = [];
  
  // Add empty cells for days before the first day of the month
  for (let i = 0; i < startingDayOfWeek; i++) {
    calendarDays.push(null);
  }
  
  // Add all days of the month
  for (let day = 1; day <= daysInMonth; day++) {
    calendarDays.push(day);
  }
  
  // Get events for a specific day
  const getEventsForDay = (day: number) => {
    const dayDate = new Date(currentYear, currentMonth, day);
    return events.filter(event => {
      if (event.startDate?.toDate) {
        const eventDate = event.startDate.toDate();
        return eventDate.toDateString() === dayDate.toDateString();
      }
      return false;
    });
  };
  
  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];
  
  const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  
  return (
    <div className="space-y-4">
      {/* Calendar Header */}
      <div className="flex items-center justify-between">
        <div className="flex gap-2">
          <Button
            variant={viewMode === 'month' ? 'default' : 'outline'}
            size="sm"
            onClick={() => {
              setViewMode('month');
              onStateChange?.(currentDate, 'month');
            }}
            data-testid="button-month-view"
          >
            Month
          </Button>
          <Button
            variant={viewMode === '3-month' ? 'default' : 'outline'}
            size="sm"
            onClick={() => {
              setViewMode('3-month');
              onStateChange?.(currentDate, '3-month');
            }}
            data-testid="button-3month-view"
          >
            3 Months
          </Button>
          <Button
            variant={viewMode === 'year' ? 'default' : 'outline'}
            size="sm"
            onClick={() => {
              setViewMode('year');
              onStateChange?.(currentDate, 'year');
            }}
            data-testid="button-year-view"
          >
            Year
          </Button>
        </div>
        
        <div className="flex items-center gap-4">
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              const newDate = viewMode === 'year' 
                ? new Date(currentYear - 1, currentMonth, 1)
                : new Date(currentYear, currentMonth - 1, 1);
              setCurrentDate(newDate);
              onStateChange?.(newDate, viewMode);
            }}
            data-testid="button-prev-period"
          >
            ← Previous
          </Button>
          <h2 className="text-lg font-semibold">
            {viewMode === 'year' ? currentYear : `${monthNames[currentMonth]} ${currentYear}`}
          </h2>
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              const newDate = viewMode === 'year' 
                ? new Date(currentYear + 1, currentMonth, 1)
                : new Date(currentYear, currentMonth + 1, 1);
              setCurrentDate(newDate);
              onStateChange?.(newDate, viewMode);
            }}
            data-testid="button-next-period"
          >
            Next →
          </Button>
        </div>
      </div>
      
      {/* Calendar Content */}
      {viewMode === 'month' && (
        <div className="grid grid-cols-7 gap-2">
          {/* Day Headers */}
          {dayNames.map(day => (
            <div key={day} className="p-2 text-center text-sm font-medium text-slate-600 border-b">
              {day}
            </div>
          ))}
          
          {/* Calendar Days */}
          {calendarDays.map((day, index) => {
            if (day === null) {
              return <div key={`empty-${index}`} className="p-2 h-24"></div>;
            }
            
            const dayEvents = getEventsForDay(day);
            const today = new Date();
            const isToday = today.getDate() === day && today.getMonth() === currentMonth && today.getFullYear() === currentYear;
            
            return (
              <div
                key={`day-${currentYear}-${currentMonth}-${day}`}
                className={`p-2 h-24 border rounded-lg ${isToday ? 'bg-blue-50 border-blue-200' : 'border-slate-200'} hover:bg-slate-50`}
                data-testid={`calendar-day-${day}`}
              >
                <div className={`text-sm font-medium mb-1 ${isToday ? 'text-blue-600' : 'text-slate-900'}`}>
                  {day}
                </div>
                <div className="space-y-1">
                  {dayEvents.slice(0, 2).map(event => (
                    <div
                      key={event.id}
                      className="text-xs p-1 rounded bg-blue-100 text-blue-800 cursor-pointer hover:bg-blue-200 truncate"
                      onClick={() => onEventClick(event.id)}
                      data-testid={`calendar-event-${event.id}`}
                    >
                      {event.title}
                    </div>
                  ))}
                  {dayEvents.length > 2 && (
                    <div className="text-xs text-slate-500">
                      +{dayEvents.length - 2} more
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
      
      {viewMode === '3-month' && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {[0, 1, 2].map(monthOffset => {
            const targetMonth = currentMonth + monthOffset;
            const targetYear = currentYear + Math.floor(targetMonth / 12);
            const adjustedMonth = ((targetMonth % 12) + 12) % 12;
            
            // Get all events for this month
            const monthEvents = events.filter(event => {
              if (event.startDate?.toDate) {
                const eventDate = event.startDate.toDate();
                return eventDate.getFullYear() === targetYear && eventDate.getMonth() === adjustedMonth;
              }
              return false;
            }).sort((a, b) => {
              const aDate = a.startDate?.toDate?.() || new Date(0);
              const bDate = b.startDate?.toDate?.() || new Date(0);
              return aDate.getTime() - bDate.getTime();
            });
            
            return (
              <div key={`3month-${targetYear}-${adjustedMonth}`} className="space-y-4">
                <h3 className="text-center font-medium text-slate-900">
                  {monthNames[adjustedMonth]} {targetYear}
                </h3>
                <div className="grid grid-cols-7 gap-1 text-xs">
                  {dayNames.map(day => (
                    <div key={`header-${day}`} className="p-1 text-center font-medium text-slate-600">
                      {day.slice(0, 1)}
                    </div>
                  ))}
                  {Array.from({ length: 42 }, (_, i) => {
                    const firstDay = new Date(targetYear, adjustedMonth, 1);
                    const dayNumber = i - firstDay.getDay() + 1;
                    const isValidDay = dayNumber > 0 && dayNumber <= new Date(targetYear, adjustedMonth + 1, 0).getDate();
                    
                    if (!isValidDay) {
                      return <div key={`empty-3month-${i}`} className="p-1 h-6"></div>;
                    }
                    
                    const dayEvents = events.filter(event => {
                      if (event.startDate?.toDate) {
                        const eventDate = event.startDate.toDate();
                        return eventDate.getFullYear() === targetYear && 
                               eventDate.getMonth() === adjustedMonth && 
                               eventDate.getDate() === dayNumber;
                      }
                      return false;
                    });
                    
                    const today = new Date();
                    const isToday = today.getDate() === dayNumber && today.getMonth() === adjustedMonth && today.getFullYear() === targetYear;
                    
                    return (
                      <div
                        key={`3month-day-${targetYear}-${adjustedMonth}-${dayNumber}`}
                        className={`p-1 h-6 text-center border rounded cursor-pointer ${isToday ? 'bg-blue-50 border-blue-200' : 'border-slate-200'} hover:bg-slate-50`}
                        onClick={() => dayEvents.length > 0 && onEventClick(dayEvents[0].id)}
                      >
                        <div className={isToday ? 'text-blue-600 font-medium' : 'text-slate-900'}>
                          {dayNumber}
                        </div>
                        {dayEvents.length > 0 && (
                          <div className="w-1 h-1 bg-blue-500 rounded-full mx-auto"></div>
                        )}
                      </div>
                    );
                  })}
                </div>
                
                {/* Event List for this month */}
                <div className="space-y-2">
                  <h4 className="text-sm font-medium text-slate-700 border-b pb-1">
                    Events ({monthEvents.length})
                  </h4>
                  {monthEvents.length === 0 ? (
                    <div className="text-xs text-slate-400 py-2">No events this month</div>
                  ) : (
                    <div className="space-y-1 max-h-32 overflow-y-auto">
                      {monthEvents.map(event => (
                        <div
                          key={`month-event-${event.id}`}
                          className="text-xs p-2 rounded bg-slate-100 hover:bg-slate-200 cursor-pointer border"
                          onClick={() => onEventClick(event.id)}
                          data-testid={`3month-event-${event.id}`}
                        >
                          <div className="font-medium text-slate-900 truncate">
                            {event.title}
                          </div>
                          <div className="text-slate-600 mt-1">
                            {event.startDate?.toDate?.().toLocaleDateString('en-US', {
                              month: 'short',
                              day: 'numeric'
                            })}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
      
      {viewMode === 'year' && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {Array.from({ length: 12 }, (_, month) => {
            const monthEvents = events.filter(event => {
              if (event.startDate?.toDate) {
                const eventDate = event.startDate.toDate();
                return eventDate.getFullYear() === currentYear && eventDate.getMonth() === month;
              }
              return false;
            });
            
            const today = new Date();
            const isCurrentMonth = today.getFullYear() === currentYear && today.getMonth() === month;
            
            return (
              <div
                key={`year-month-${month}`}
                className={`p-4 border rounded-lg hover:bg-slate-50 cursor-pointer ${isCurrentMonth ? 'bg-blue-50 border-blue-200' : 'border-slate-200'}`}
                onClick={() => monthEvents.length > 0 && onEventClick(monthEvents[0].id)}
                data-testid={`calendar-year-month-${month}`}
              >
                <h4 className={`font-medium mb-2 ${isCurrentMonth ? 'text-blue-600' : 'text-slate-900'}`}>
                  {monthNames[month]}
                </h4>
                <div className="space-y-1">
                  {monthEvents.slice(0, 3).map(event => (
                    <div
                      key={event.id}
                      className="text-xs p-1 rounded bg-blue-100 text-blue-800 truncate"
                      onClick={(e) => {
                        e.stopPropagation();
                        onEventClick(event.id);
                      }}
                    >
                      {event.title}
                    </div>
                  ))}
                  {monthEvents.length > 3 && (
                    <div className="text-xs text-slate-500">
                      +{monthEvents.length - 3} more
                    </div>
                  )}
                  {monthEvents.length === 0 && (
                    <div className="text-xs text-slate-400">
                      No events
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default function Events() {
  const [events, setEvents] = useState<CommunityEvent[]>([]);
  const [filteredEvents, setFilteredEvents] = useState<CommunityEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [pendingImageUpload, setPendingImageUpload] = useState<{ file: File; field: any } | null>(null);
  const { user } = useAuth();
  const { toast } = useToast();
  const [, navigate] = useLocation();

  // Initialize state from URL parameters
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [viewMode, setViewMode] = useState<'list' | 'calendar'>('list');

  // Restore state from URL on component mount
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    setStatusFilter(urlParams.get('status') || 'all');
    setTypeFilter(urlParams.get('type') || 'all');
    setViewMode((urlParams.get('view') as 'list' | 'calendar') || 'list');
  }, []);

  // Function to update URL params
  const updateUrlParams = (updates: Record<string, string>) => {
    const currentParams = new URLSearchParams(window.location.search);
    Object.entries(updates).forEach(([key, value]) => {
      // Only delete if it's a filter parameter with default value
      if ((key === 'status' || key === 'type') && value === 'all') {
        currentParams.delete(key);
      } else if (key === 'view' && value === 'list') {
        currentParams.delete(key);
      } else {
        currentParams.set(key, value);
      }
    });
    const newUrl = currentParams.toString() ? `${window.location.pathname}?${currentParams.toString()}` : window.location.pathname;
    window.history.replaceState({}, '', newUrl);
  };

  const form = useForm<EventFormData>({
    resolver: zodResolver(eventSchema),
    defaultValues: {
      title: '',
      description: '',
      type: 'liveQA' as const,
      tag: '',
      dateMode: 'dateRange' as const,
      selectedMonth: '',
      startDate: '',
      endDate: '',
      startTime: '',
      endTime: '',
      url: '',
      enableMessages: false,
      enableImages: false,
      imageUrl: '',
    },
  });

  const watchedType = form.watch('type');
  const watchedDateMode = form.watch('dateMode');

  useEffect(() => {
    if (!user) return;

    const loadData = async () => {
      try {
        logger.log('📅 Loading events...');
        const eventsData = await getCommunityEvents() as CommunityEvent[];
        logger.log('📋 Events loaded:', eventsData.length);
        const eventsWithStatus = eventsData.map(event => ({ ...event, status: calculateEventStatus(event) }));
        const sortedEvents = eventsWithStatus.sort((a, b) => {
          const aDate = getEventSortDate(a);
          const bDate = getEventSortDate(b);
          return bDate.getTime() - aDate.getTime();
        });
        setEvents(sortedEvents);
      } catch (error: any) {
        logger.error('❌ Error loading events:', error);
        toast({ title: "Failed to load events", description: error.message, variant: "destructive" });
      } finally {
        setLoading(false);
      }
    };

    loadData();
    const timer = setInterval(loadData, 15_000);
    return () => clearInterval(timer);
  }, [user, toast]);

  // Filter events based on status and type
  useEffect(() => {
    let filtered = events;
    
    if (statusFilter !== 'all') {
      filtered = filtered.filter(event => event.status === statusFilter);
    }
    
    if (typeFilter !== 'all') {
      filtered = filtered.filter(event => event.type === typeFilter);
    }
    
    setFilteredEvents(filtered);
  }, [events, statusFilter, typeFilter]);

  // Get unique status and type values for filter chips
  const statusCounts = events.reduce((acc, event) => {
    const status = event.status || 'Unknown';
    acc[status] = (acc[status] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);

  const typeCounts = events.reduce((acc, event) => {
    const type = event.type || 'Unknown';
    acc[type] = (acc[type] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);

  const getEventSortDate = (event: CommunityEvent): Date => {
    // Use startDate for sorting, fallback to endDate, then createdAt
    if (event.startDate) {
      return event.startDate?.toDate ? event.startDate.toDate() : new Date(event.startDate);
    }
    if (event.endDate) {
      return event.endDate?.toDate ? event.endDate.toDate() : new Date(event.endDate);
    }
    return event.createdAt?.toDate?.() || new Date(0);
  };

  const calculateEventStatus = (event: CommunityEvent): 'Scheduled' | 'Current' | 'Completed' => {
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Start of today
    
    let startDate: Date | null = null;
    let endDate: Date | null = null;
    
    // Parse start date
    if (event.startDate) {
      if (event.startDate.toDate) {
        startDate = event.startDate.toDate();
      } else {
        startDate = new Date(event.startDate);
      }
      startDate?.setHours(0, 0, 0, 0);
    }
    
    // Parse end date
    if (event.endDate) {
      if (event.endDate.toDate) {
        endDate = event.endDate.toDate();
      } else {
        endDate = new Date(event.endDate);
      }
      endDate?.setHours(23, 59, 59, 999); // End of day
    }
    
    // Check if completed
    if (endDate && endDate < today) {
      return 'Completed';
    }
    
    // Check if scheduled
    if (startDate && startDate > today) {
      return 'Scheduled';
    }
    
    // Otherwise it's current (includes ongoing with null end dates)
    return 'Current';
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Current':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'Completed':
        return 'bg-slate-100 text-slate-800 border-slate-200';
      case 'Scheduled':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      default:
        return 'bg-slate-100 text-slate-800 border-slate-200';
    }
  };

  const handleAdd = () => {
    form.reset({
      title: '',
      description: '',
      type: 'liveQA',
      tag: '',
      dateMode: 'dateRange',
      selectedMonth: '',
      startDate: '',
      endDate: '',
      startTime: '',
      endTime: '',
      url: '',
      enableMessages: false,
      enableImages: false,
      imageUrl: '',
    });
    setIsAddModalOpen(true);
  };

  const handleImageUpload = async (file: File): Promise<string> => {
    setUploadingImage(true);
    try {
      return await uploadEventBackground(file);
    } catch (error) {
      logger.error('Error uploading image:', error);
      throw error;
    } finally {
      setUploadingImage(false);
    }
  };

  const retryImageUpload = async () => {
    if (!pendingImageUpload) return;
    const { file, field } = pendingImageUpload;
    try {
      const downloadURL = await handleImageUpload(file);
      field.onChange(downloadURL);
      setPendingImageUpload(null);
      toast({
        title: "Image uploaded",
        description: "Image has been uploaded successfully.",
      });
    } catch (error) {
      toast({
        title: "Upload failed",
        description: formatApiError(error, "upload image"),
        variant: "destructive",
        action: (
          <ToastAction altText="Retry" onClick={retryImageUpload}>
            Retry
          </ToastAction>
        ),
      });
    }
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>, field: any) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast({
        title: "Invalid file type",
        description: "Please select an image file.",
        variant: "destructive",
      });
      return;
    }

    try {
      const downloadURL = await handleImageUpload(file);
      field.onChange(downloadURL);
      setPendingImageUpload(null);
      toast({
        title: "Image uploaded",
        description: "Image has been uploaded successfully.",
      });
    } catch (error) {
      setPendingImageUpload({ file, field });
      toast({
        title: "Upload failed",
        description: formatApiError(error, "upload image"),
        variant: "destructive",
        action: (
          <ToastAction altText="Retry" onClick={() => retryImageUpload()}>
            Retry
          </ToastAction>
        ),
      });
    }
  };

  const generateMonthOptions = () => {
    const months = [];
    const currentYear = new Date().getFullYear();
    
    for (let year = currentYear; year <= currentYear + 2; year++) {
      for (let month = 0; month < 12; month++) {
        const date = new Date(year, month, 1);
        const monthString = date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
        months.push({
          value: `${year}-${String(month + 1).padStart(2, '0')}`,
          label: monthString
        });
      }
    }
    return months;
  };

  const handleSubmit = async (data: EventFormData) => {
    try {
      let startDate: Timestamp | null = null;
      let endDate: Timestamp | null = null;
      let monthField: string | null = null;

      if (data.type === 'stepsChallenge' || data.type === 'content') {
        if (data.dateMode === 'month' && data.selectedMonth) {
          // Handle month selection for steps challenge and Content
          const [year, month] = data.selectedMonth.split('-');
          const startOfMonth = new Date(parseInt(year), parseInt(month) - 1, 1);
          const endOfMonth = new Date(parseInt(year), parseInt(month), 0);
          startDate = Timestamp.fromDate(startOfMonth);
          endDate = Timestamp.fromDate(endOfMonth);
          monthField = data.selectedMonth; // Save as YYYY-MM format
        } else if (data.startDate && data.endDate) {
          // Handle date range for steps challenge and Content
          startDate = Timestamp.fromDate(new Date(data.startDate));
          endDate = Timestamp.fromDate(new Date(data.endDate));
        }
      } else {
        // Handle date and time for other event types (liveQA, yoga)
        if (data.startDate && data.startTime) {
          const startDateTime = new Date(`${data.startDate}T${data.startTime}`);
          startDate = Timestamp.fromDate(startDateTime);
        }
        if (data.startDate && data.endTime) {
          // Use the same date for end time
          const endDateTime = new Date(`${data.startDate}T${data.endTime}`);
          endDate = Timestamp.fromDate(endDateTime);
        }
      }

      const eventData = {
        title: data.title,
        description: data.description || '',
        type: data.type,
        tag: data.type === 'content' ? data.tag || '' : undefined,
        startDate,
        endDate,
        month: monthField,
        url: data.type !== 'stepsChallenge' && data.type !== 'content' ? data.url || '' : undefined,
        enableMessageBoard: data.type !== 'content' ? data.enableMessages : false,
        enableImageUpload: data.type !== 'content' ? data.enableImages : false,
        backgroundImageUrl: data.imageUrl || '',
        added: Timestamp.now(),
      };

      // Remove undefined values
      const cleanEventData = Object.fromEntries(
        Object.entries(eventData).filter(([_, value]) => value !== undefined)
      );

      await addCommunityEvent(cleanEventData);
      
      toast({
        title: "Event created",
        description: "New event has been created successfully.",
      });
      
      setIsAddModalOpen(false);
      form.reset();
    } catch (error) {
      logger.error('Error saving event:', error);
      toast({
        title: "Error creating event",
        description: formatApiError(error, "create event"),
        variant: "destructive",
      });
    }
  };

  const formatEventDate = (event: CommunityEvent): string => {
    // If month field has a value, show year and month
    if (event.month) {
      return event.month;
    }

    // If both startDate and endDate exist
    if (event.startDate && event.endDate) {
      let startDate: Date;
      let endDate: Date;

      try {
        // Handle Firestore Timestamp
        if (event.startDate?.toDate) {
          startDate = event.startDate.toDate();
        } else {
          startDate = new Date(event.startDate);
        }

        if (event.endDate?.toDate) {
          endDate = event.endDate.toDate();
        } else {
          endDate = new Date(event.endDate);
        }

        // Check if startDate equals endDate (same day)
        if (startDate.toDateString() === endDate.toDateString()) {
          return startDate.toLocaleDateString();
        } else {
          // Date range
          return `${startDate.toLocaleDateString()} - ${endDate.toLocaleDateString()}`;
        }
      } catch (error) {
        return 'Invalid date';
      }
    }

    // If only startDate exists
    if (event.startDate) {
      try {
        const date = event.startDate?.toDate ? event.startDate.toDate() : new Date(event.startDate);
        return date.toLocaleDateString();
      } catch (error) {
        return 'Invalid date';
      }
    }

    return 'No date set';
  };

  if (loading) {
    return (
      <div className="flex-1 p-8">
        <div className="max-w-7xl mx-auto">
          <div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full mx-auto mb-4"></div>
          <p className="text-center text-slate-600">Loading events...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 p-8">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
              <Calendar className="h-6 w-6" />
              Events
            </h1>
            <p className="text-slate-600 mt-1">
              Community events and activities
            </p>
          </div>
          <Button onClick={handleAdd} className="flex items-center gap-2">
            <Plus className="h-4 w-4" />
            Add Event
          </Button>
        </div>

        {/* Status Filter Chips */}
        <div className="flex flex-wrap gap-2 mb-4">
          <div className="flex items-center gap-2 mr-4">
            <span className="text-sm font-medium text-slate-600">Status:</span>
            <Badge
              variant={statusFilter === 'all' ? 'default' : 'secondary'}
              className="cursor-pointer px-3 py-1"
              onClick={() => {
                setStatusFilter('all');
                updateUrlParams({ status: 'all' });
              }}
            >
              All ({events.length})
            </Badge>
            {['Current', 'Scheduled', 'Completed'].map((status) => (
              <Badge
                key={status}
                variant={statusFilter === status ? 'default' : 'secondary'}
                className={`cursor-pointer px-3 py-1 ${statusFilter === status ? '' : 'hover:bg-slate-200'}`}
                onClick={() => {
                  setStatusFilter(status);
                  updateUrlParams({ status });
                }}
              >
                {status} ({statusCounts[status] || 0})
              </Badge>
            ))}
          </div>
        </div>

        {/* Type Filter Chips */}
        <div className="flex flex-wrap gap-2">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-slate-600">Type:</span>
            <Badge
              variant={typeFilter === 'all' ? 'default' : 'secondary'}
              className="cursor-pointer px-3 py-1"
              onClick={() => {
                setTypeFilter('all');
                updateUrlParams({ type: 'all' });
              }}
            >
              All ({events.length})
            </Badge>
            {Object.entries(typeCounts).map(([type, count]) => (
              <Badge
                key={type}
                variant={typeFilter === type ? 'default' : 'secondary'}
                className={`cursor-pointer px-3 py-1 ${typeFilter === type ? '' : 'hover:bg-slate-200'}`}
                onClick={() => {
                  setTypeFilter(type);
                  updateUrlParams({ type });
                }}
              >
                {type} ({count})
              </Badge>
            ))}
          </div>
        </div>

        {/* Events Table */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-lg">All Events</CardTitle>
                <CardDescription>
                  {viewMode === 'list' ? 'List of all community events in the system' : 'Calendar view of all community events'}
                </CardDescription>
              </div>
              <div className="flex gap-2">
                <Button
                  variant={viewMode === 'list' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => {
                    setViewMode('list');
                    updateUrlParams({ view: 'list' });
                  }}
                  data-testid="button-list-view"
                >
                  <List className="h-4 w-4 mr-2" />
                  List
                </Button>
                <Button
                  variant={viewMode === 'calendar' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => {
                    setViewMode('calendar');
                    updateUrlParams({ view: 'calendar' });
                  }}
                  data-testid="button-calendar-view"
                >
                  <CalendarDays className="h-4 w-4 mr-2" />
                  Calendar
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {viewMode === 'list' ? (
              // List View
              filteredEvents.length === 0 ? (
                <div className="text-center py-12">
                  <Calendar className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-slate-900 mb-2">
                    {statusFilter === 'all' && typeFilter === 'all' ? 'No events found' : 'No matching events'}
                  </h3>
                  <p className="text-slate-600 mb-4">
                    {statusFilter === 'all' && typeFilter === 'all' 
                      ? 'No community events have been added yet.' 
                      : 'No events match the selected filters.'}
                  </p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Title</TableHead>
                        <TableHead>Type</TableHead>
                        <TableHead className="w-24">Status</TableHead>
                        <TableHead>Date</TableHead>
                        <TableHead className="w-20">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredEvents.map((event) => (
                        <TableRow key={event.id} data-testid={`row-event-${event.id}`}>
                          <TableCell>
                            <div className="font-medium text-slate-900" data-testid={`text-title-${event.id}`}>
                              {event.title || 'Untitled Event'}
                            </div>
                          </TableCell>
                          <TableCell>
                            <Badge variant="secondary" data-testid={`badge-type-${event.id}`}>
                              {event.type || 'Unknown'}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <Badge className={getStatusColor(event.status!)} data-testid={`badge-status-${event.id}`}>
                              {event.status}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2 text-sm" data-testid={`text-date-${event.id}`}>
                              <Calendar className="h-3 w-3 text-slate-400" />
                              <span className="font-medium">
                                {formatEventDate(event)}
                              </span>
                            </div>
                          </TableCell>
                          <TableCell>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                              // Save current state in URL params when navigating
                              const currentParams = new URLSearchParams(window.location.search);
                              currentParams.set('status', statusFilter);
                              currentParams.set('type', typeFilter);
                              currentParams.set('view', viewMode);
                              navigate(`/events/details?id=${event.id}&returnParams=${encodeURIComponent(currentParams.toString())}`);
                            }}
                              className="h-8 w-8 p-0"
                              data-testid={`button-edit-event-${event.id}`}
                            >
                              <Edit2 className="h-4 w-4" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )
            ) : (
              // Calendar View
              <CalendarView 
                events={filteredEvents} 
                onEventClick={(eventId) => {
                  // Save current state in URL params when navigating
                  const currentParams = new URLSearchParams(window.location.search);
                  currentParams.set('status', statusFilter);
                  currentParams.set('type', typeFilter);
                  currentParams.set('view', viewMode);
                  navigate(`/events/details?id=${eventId}&returnParams=${encodeURIComponent(currentParams.toString())}`);
                }}
                onStateChange={(calendarDate, calendarViewMode) => {
                  // Update URL with calendar state
                  updateUrlParams({
                    calendarDate: calendarDate.toISOString(),
                    calendarView: calendarViewMode
                  });
                }}
              />
            )}
          </CardContent>
        </Card>

        {/* Add Event Modal */}
        <Dialog open={isAddModalOpen} onOpenChange={setIsAddModalOpen}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Add New Event</DialogTitle>
              <DialogDescription>
                Create a new community event with all the necessary details.
              </DialogDescription>
            </DialogHeader>
            <Form {...form}>
              <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-6">
                {/* Event Type - Moved to Top */}
                <div className="space-y-4">
                  <FormField
                    control={form.control}
                    name="type"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Event Type *</FormLabel>
                        <Select onValueChange={field.onChange} value={field.value}>
                          <FormControl>
                            <SelectTrigger data-testid="select-type">
                              <SelectValue placeholder="Select event type" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="liveQA">Live Q&A</SelectItem>
                            <SelectItem value="stepsChallenge">Steps Challenge</SelectItem>
                            <SelectItem value="yoga">Yoga</SelectItem>
                            <SelectItem value="content">Content</SelectItem>
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                {/* Basic Information */}
                <div className="space-y-4">
                  <h3 className="font-semibold text-slate-900">Basic Information</h3>
                  
                  <FormField
                    control={form.control}
                    name="title"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Title *</FormLabel>
                        <FormControl>
                          <Input {...field} placeholder="Enter event title" data-testid="input-title" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="description"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Description</FormLabel>
                        <FormControl>
                          <Textarea {...field} placeholder="Enter event description" data-testid="input-description" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  {/* Tag field for Content type */}
                  {watchedType === 'content' && (
                    <FormField
                      control={form.control}
                      name="tag"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Tag</FormLabel>
                          <FormControl>
                            <Input {...field} placeholder="Enter content tag" data-testid="input-tag" />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  )}
                </div>

                {/* Date/Time Configuration */}
                <div className="space-y-4">
                  <h3 className="font-semibold text-slate-900">Date & Time</h3>
                  
                  {(watchedType === 'stepsChallenge' || watchedType === 'content') && (
                    <FormField
                      control={form.control}
                      name="dateMode"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Date Selection Mode</FormLabel>
                          <Select onValueChange={field.onChange} value={field.value}>
                            <FormControl>
                              <SelectTrigger data-testid="select-date-mode">
                                <SelectValue placeholder="Select date mode" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              <SelectItem value="month">Select Month</SelectItem>
                              <SelectItem value="dateRange">Date Range</SelectItem>
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  )}

                  {(watchedType === 'stepsChallenge' || watchedType === 'content') && watchedDateMode === 'month' && (
                    <FormField
                      control={form.control}
                      name="selectedMonth"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Select Month</FormLabel>
                          <Select onValueChange={field.onChange} value={field.value}>
                            <FormControl>
                              <SelectTrigger data-testid="select-month">
                                <SelectValue placeholder="Select month" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              {generateMonthOptions().map((month) => (
                                <SelectItem key={month.value} value={month.value}>
                                  {month.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  )}

                  {(watchedType === 'stepsChallenge' || watchedType === 'content') && watchedDateMode === 'dateRange' && (
                    <div className="grid grid-cols-2 gap-4">
                      <FormField
                        control={form.control}
                        name="startDate"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Start Date</FormLabel>
                            <FormControl>
                              <Input type="date" {...field} data-testid="input-start-date" />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="endDate"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>End Date</FormLabel>
                            <FormControl>
                              <Input type="date" {...field} data-testid="input-end-date" />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                  )}

                  {(watchedType === 'liveQA' || watchedType === 'yoga') && (
                    <>
                      <FormField
                        control={form.control}
                        name="startDate"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Event Date</FormLabel>
                            <FormControl>
                              <Input type="date" {...field} data-testid="input-event-date" />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <div className="grid grid-cols-2 gap-4">
                        <FormField
                          control={form.control}
                          name="startTime"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Start Time</FormLabel>
                              <FormControl>
                                <Input type="time" {...field} data-testid="input-start-time" />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />

                        <FormField
                          control={form.control}
                          name="endTime"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>End Time</FormLabel>
                              <FormControl>
                                <Input type="time" {...field} data-testid="input-end-time" />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>
                    </>
                  )}
                </div>

                {/* URL Field (not for steps challenges or Content) */}
                {watchedType !== 'stepsChallenge' && watchedType !== 'content' && (
                  <div className="space-y-4">
                    <h3 className="font-semibold text-slate-900">Event URL</h3>
                    <FormField
                      control={form.control}
                      name="url"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>URL</FormLabel>
                          <FormControl>
                            <Input {...field} placeholder="Enter event URL" data-testid="input-url" />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                )}

                {/* Settings - Hidden for Content type */}
                {watchedType !== 'content' && (
                  <div className="space-y-4">
                    <h3 className="font-semibold text-slate-900">Settings</h3>
                    
                    <FormField
                      control={form.control}
                      name="enableMessages"
                      render={({ field }) => (
                        <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                          <div className="space-y-0.5">
                            <FormLabel className="text-base">Enable Messages</FormLabel>
                            <div className="text-sm text-slate-600">
                              Allow participants to send messages during the event
                            </div>
                          </div>
                          <FormControl>
                            <Switch
                              checked={field.value}
                              onCheckedChange={field.onChange}
                              data-testid="switch-enable-messages"
                            />
                          </FormControl>
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="enableImages"
                      render={({ field }) => (
                        <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                          <div className="space-y-0.5">
                            <FormLabel className="text-base">Enable Images</FormLabel>
                            <div className="text-sm text-slate-600">
                              Allow participants to share images during the event
                            </div>
                          </div>
                          <FormControl>
                            <Switch
                              checked={field.value}
                              onCheckedChange={field.onChange}
                              data-testid="switch-enable-images"
                            />
                          </FormControl>
                        </FormItem>
                      )}
                    />
                  </div>
                )}

                {/* Image Upload */}
                <div className="space-y-4">
                  <h3 className="font-semibold text-slate-900">Event Image</h3>
                  <FormField
                    control={form.control}
                    name="imageUrl"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Background Image</FormLabel>
                        <FormControl>
                          <div className="space-y-4">
                            <Input
                              type="file"
                              accept="image/*"
                              onChange={(e) => handleFileChange(e, field)}
                              disabled={uploadingImage}
                              data-testid="input-image-upload"
                            />
                            {uploadingImage && (
                              <div className="text-sm text-slate-600">Uploading image...</div>
                            )}
                            {field.value && (
                              <div className="flex items-center gap-2 text-sm text-green-600">
                                <ImageIcon className="h-4 w-4" />
                                Image uploaded successfully
                              </div>
                            )}
                          </div>
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                {/* Submit Button */}
                <div className="flex justify-end gap-3 pt-4">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setIsAddModalOpen(false)}
                    data-testid="button-cancel"
                  >
                    Cancel
                  </Button>
                  <Button type="submit" disabled={uploadingImage} data-testid="button-submit">
                    {uploadingImage ? 'Creating...' : 'Create Event'}
                  </Button>
                </div>
              </form>
            </Form>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}